import { useState, useEffect, useCallback } from 'react';
import { getItems, createItem, updateItem } from '../fitness/fitnessApi';

const LIST = 'EventMissions';

function safeParse(json) {
  try { return JSON.parse(json) || []; } catch { return []; }
}

export function useEventMissions(getToken, userEmail) {
  const [missions, setMissions] = useState({}); // eventId -> mission object
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchMissions = useCallback(async () => {
    try {
      setError(null);
      const token = await getToken();
      const items = await getItems(token, LIST);
      const byEvent = {};
      items
        .filter(it => (it.CreatedBy || '').toLowerCase() === userEmail.toLowerCase())
        .forEach(it => {
          byEvent[it.EventId] = {
            id: it.id,
            eventId: it.EventId,
            eventTitle: it.EventTitle || '',
            eventDate: it.EventDate || '',
            eventLocation: it.EventLocation || '',
            templateId: it.TemplateId || '',
            templateName: it.Title || '',
            tasks: safeParse(it.TaskStatus),
            additionalTasks: safeParse(it.AdditionalTasks),
            postItLinks: safeParse(it.PostItLinks),
            roleTag: it.RoleTag || '',
            skipped: it.Title === '__skipped__',
          };
        });
      setMissions(byEvent);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken, userEmail]);

  useEffect(() => { fetchMissions(); }, [fetchMissions]);

  const attachTemplate = useCallback(async (event, template) => {
    try {
      setError(null);
      const token = await getToken();
      const taskStatus = template.tasks.map(t => ({
        name: t.name,
        timeOffset: t.timeOffset,
        notes: t.notes || '',
        vaultDocPath: t.vaultDocPath || '',
        status: 'pending',
        completedAt: null,
      }));
      const created = await createItem(token, LIST, {
        Title: template.name,
        EventId: event.id,
        EventTitle: event.title,
        EventDate: event.start ? event.start.toISOString() : '',
        EventLocation: event.location || '',
        TemplateId: template.id || '',
        TaskStatus: JSON.stringify(taskStatus),
        AdditionalTasks: '[]',
        PostItLinks: '[]',
        RoleTag: template.defaultRole || '',
        CreatedBy: userEmail,
      });
      setMissions(prev => ({
        ...prev,
        [event.id]: {
          id: created.id,
          eventId: event.id,
          eventTitle: event.title,
          eventDate: event.start ? event.start.toISOString() : '',
          eventLocation: event.location || '',
          templateId: template.id || '',
          templateName: template.name,
          tasks: taskStatus,
          additionalTasks: [],
          roleTag: template.defaultRole || '',
          skipped: false,
        },
      }));
    } catch (err) {
      setError(err.message);
    }
  }, [getToken, userEmail]);

  const skipEvent = useCallback(async (event) => {
    try {
      setError(null);
      const token = await getToken();
      const created = await createItem(token, LIST, {
        Title: '__skipped__',
        EventId: event.id,
        EventTitle: event.title,
        EventDate: event.start ? event.start.toISOString() : '',
        EventLocation: '',
        TemplateId: '',
        TaskStatus: '[]',
        AdditionalTasks: '[]',
        PostItLinks: '[]',
        RoleTag: '',
        CreatedBy: userEmail,
      });
      setMissions(prev => ({
        ...prev,
        [event.id]: { id: created.id, eventId: event.id, skipped: true, tasks: [], additionalTasks: [] },
      }));
    } catch (err) {
      setError(err.message);
    }
  }, [getToken, userEmail]);

  const addSubTask = useCallback(async (eventId, task) => {
    const mission = missions[eventId];
    if (!mission || !mission.id) return;
    try {
      setError(null);
      const token = await getToken();
      const updated = [...(mission.additionalTasks || []), {
        name: task.name,
        timeOffset: task.timeOffset || -30,
        notes: task.notes || '',
        vaultDocPath: task.vaultDocPath || '',
        status: 'pending',
        completedAt: null,
      }];
      await updateItem(token, LIST, mission.id, {
        AdditionalTasks: JSON.stringify(updated),
      });
      setMissions(prev => ({
        ...prev,
        [eventId]: { ...prev[eventId], additionalTasks: updated },
      }));
    } catch (err) {
      setError(err.message);
    }
  }, [getToken, missions]);

  const updateTaskStatus = useCallback(async (eventId, taskIndex, isAdditional, status) => {
    const mission = missions[eventId];
    if (!mission || !mission.id) return;
    try {
      setError(null);
      const token = await getToken();
      const field = isAdditional ? 'additionalTasks' : 'tasks';
      const list = [...(mission[field] || [])];
      list[taskIndex] = {
        ...list[taskIndex],
        status,
        completedAt: status === 'done' ? new Date().toISOString() : null,
      };
      const spField = isAdditional ? 'AdditionalTasks' : 'TaskStatus';
      await updateItem(token, LIST, mission.id, {
        [spField]: JSON.stringify(list),
      });
      setMissions(prev => ({
        ...prev,
        [eventId]: { ...prev[eventId], [field]: list },
      }));
    } catch (err) {
      setError(err.message);
    }
  }, [getToken, missions]);

  function getMission(eventId) {
    return missions[eventId] || null;
  }

  function getAllTasks(eventId) {
    const m = missions[eventId];
    if (!m) return [];
    const templateTasks = (m.tasks || []).map((t, i) => ({ ...t, index: i, isAdditional: false }));
    const addedTasks = (m.additionalTasks || []).map((t, i) => ({ ...t, index: i, isAdditional: true }));
    return [...templateTasks, ...addedTasks];
  }

  const linkPostIt = useCallback(async (eventId, postItId) => {
    const mission = missions[eventId];
    if (!mission || !mission.id) return;
    try {
      setError(null);
      const token = await getToken();
      const current = mission.postItLinks || [];
      if (current.includes(postItId)) return; // already linked
      const updated = [...current, postItId];
      await updateItem(token, LIST, mission.id, {
        PostItLinks: JSON.stringify(updated),
      });
      setMissions(prev => ({
        ...prev,
        [eventId]: { ...prev[eventId], postItLinks: updated },
      }));
    } catch (err) {
      setError(err.message);
    }
  }, [getToken, missions]);

  const unlinkPostIt = useCallback(async (eventId, postItId) => {
    const mission = missions[eventId];
    if (!mission || !mission.id) return;
    try {
      setError(null);
      const token = await getToken();
      const updated = (mission.postItLinks || []).filter(id => id !== postItId);
      await updateItem(token, LIST, mission.id, {
        PostItLinks: JSON.stringify(updated),
      });
      setMissions(prev => ({
        ...prev,
        [eventId]: { ...prev[eventId], postItLinks: updated },
      }));
    } catch (err) {
      setError(err.message);
    }
  }, [getToken, missions]);

  return {
    missions,
    loading,
    error,
    getMission,
    getAllTasks,
    attachTemplate,
    skipEvent,
    linkPostIt,
    unlinkPostIt,
    addSubTask,
    updateTaskStatus,
    refetch: fetchMissions,
  };
}
